'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';

import TicketButtonGrouped from '@/app/[locale]/(default-layout)/program/_components/TicketButtonGrouped';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';
import CardWrapper from '@/components/CardWrapper.tsx';
import BellSubscribeButton from '@/components/newsletter/BellSubscribeButton';

import { HrefOrHrefWithParams } from 'next-intl/dist/types/navigation/shared/utils';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFutureShowings } from '@/hooks/useFutureShowings';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';
import DotSeparatedLabel from '@cineamo/ui/src/label/DotSeparatedLabel';
import Tooltip from '@cineamo/ui/src/tooltip/Tooltip';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { Link } from '@/i18n/routing';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { getLogicalStartOfDay } from '@/utils/date-helper';

const MAX_VISIBLE_SHOWINGS = 4;
const INITIAL_SHOWINGS_COUNT = 1;

export default function PremiereSectionItem(props: { content: CachedContentData }): React.JSX.Element {
    const { content } = props;
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;
    const contentId = content.id;

    const t = useTranslations();
    const format = useFormat();

    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const { data: showingsResponse } = useFutureShowings(cinemaId, { contentId: contentId });

    const premiereDate = content?.releaseDate;

    const isPremiereShowing = (showing: { startDatetime?: string }, premiereDate: string | null | undefined) => {
        if (!showing.startDatetime || !premiereDate) return false;
        const showingDate = getLogicalStartOfDay(new Date(showing.startDatetime));
        const releaseDate = getLogicalStartOfDay(premiereDate);
        return showingDate.getTime() <= releaseDate.getTime();
    };

    const showings = useMemo(
        () => showingsResponse?.filter((showing) => isPremiereShowing(showing, premiereDate)) ?? [],
        [showingsResponse, premiereDate]
    );
    const visibleShowings = useMemo(
        () => (isExpanded ? showings.slice(0, MAX_VISIBLE_SHOWINGS) : showings.slice(0, INITIAL_SHOWINGS_COUNT)),
        [showings, isExpanded]
    );

    const officialRelease = useMemo(() => {
        return t('home.premiere-date', {
            date: format.dateTime.formatDate(new Date(premiereDate ?? ''), {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            })
        });
    }, [t, premiereDate, format]);

    const showingName = content?.title;
    const showingBackdropImageUrl = content?.backdropImage;
    const showingPosterImage = content?.posterImage;
    const showingInfo = [
        officialRelease
        // getMovieAgeRating(movie, countryCode),
        // runtime ? `${runtime} ${t('common.time.minute-short')}` : null
    ].filter((it) => !!it);

    const showingsCount = showings.length;

    return (
        <div className="relative flex flex-col group">
            <CardWrapper className="relative flex-none w-[290px] lg:w-[330px] min-h-[375px] lg:min-h-[412px] border-2 border-[var(--surface-color)] rounded-8 overflow-hidden flex flex-col gap-8">
                <BellSubscribeButton
                    topic={{ type: 'movie', referenceId: String(contentId), label: showingName || '' }}
                    className="absolute top-8 right-8 z-10"
                />
                <Link
                    className="h-[165px] lg:h-[192px] flex-none"
                    href={
                        {
                            pathname: '/program/[contentId]',
                            params: {
                                contentId: contentId
                            }
                        } as HrefOrHrefWithParams<string>
                    }
                    /*
                     * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
                     * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
                     */
                    aria-label={showingName}
                    prefetch={false}>
                    <div
                        className={classnames(
                            'relative w-full h-full',
                            'gradient-mask-b-[#D9D9D9_59.5%,transparent_92.93%]'
                        )}>
                        <ImageWithTransition
                            className={classnames('w-full h-full object-cover group-hover:scale-105 duration-200')}
                            src={showingBackdropImageUrl ?? ''}
                            alt={`${showingName} Backdrop`}
                            width={330}
                            height={192}
                        />
                        {/* Dark gradient overlay for mobile */}
                        <div
                            className={classnames('absolute w-1/2 h-full left-0 top-0 bg-black/85 gradient-mask-r-60')}
                        />
                    </div>
                    {/* Poster visible only for mobile */}
                    <div
                        className={classnames(
                            'absolute',
                            'left-8 top-0',
                            'my-8',
                            'h-[141px] lg:h-[168px] aspect-[2/3]',
                            'rounded-3 lg:rounded-8 overflow-hidden'
                        )}>
                        <ImageWithTransition
                            className="w-full h-full object-cover"
                            src={showingPosterImage ?? ''}
                            alt={`${showingName} Poster`}
                            width={150}
                            height={200}
                        />
                    </div>
                </Link>
                <div className="w-full flex flex-col space-y-0 px-12 justify-between">
                    <div className="w-full flex flex-col pb-16 flex-auto justify-center text-[var(--on-surface-color)]">
                        <Tooltip
                            tooltipText={showingName}
                            isDisabled={(showingName?.length ?? 0) < 25}
                            usePortal>
                            <h3 className="text-body-bold-16 whitespace-nowrap text-ellipsis truncate">
                                {showingName}
                            </h3>
                        </Tooltip>
                        {showingInfo.length > 0 && (
                            <DotSeparatedLabel
                                className="text-body-12 whitespace-nowrap text-ellipsis truncate opacity-80 leading-[20px]"
                                labels={showingInfo}
                            />
                        )}
                    </div>

                    <div className="flex flex-col gap-8">
                        <div
                            className={classnames(
                                'w-full grid grid-cols-1 gap-8 h-[var(--cards-height)] overflow-hidden duration-200'
                            )}
                            style={
                                {
                                    '--cards-height': `${visibleShowings.length * 120 + (visibleShowings.length - 1) * 8}px`
                                } as React.CSSProperties
                            }>
                            {visibleShowings.map((groupedShowing) => (
                                <TicketButtonGrouped
                                    key={`TicketButtonGrouped#${groupedShowing.id}`}
                                    content={content}
                                    className="flex-none h-[120px] text-[var(--on-surface-color)]"
                                    // groupedShowing={groupedShowing}
                                    showing={groupedShowing}
                                    showTags
                                    showDateAndTime
                                    showingTagStyle={{
                                        backgroundColor: domainConfiguration.style.primaryColor,
                                        color: domainConfiguration.style.onPrimaryColor
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {isExpanded && showingsCount > MAX_VISIBLE_SHOWINGS && (
                    <Link
                        className={classnames(
                            'w-full flex justify-center overflow-hidden',
                            isExpanded ? 'h-[46px]' : 'h-[0px]'
                        )}
                        href={
                            {
                                pathname: '/program/[contentId]',
                                params: {
                                    contentId: contentId
                                }
                            } as HrefOrHrefWithParams<string>
                        }
                        /*
                         * Prefetch the full page data beyond the loading.js boundaries by setting the prefetch prop to true
                         * (See: https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#2-prefetching)
                         */
                        aria-label={showingName}
                        prefetch={false}>
                        <Button
                            style={ButtonStyle.text_only}
                            labelClassName="text-body-bold-14"
                            css={{
                                color: domainConfiguration.style.onBackgroundColor
                            }}>
                            {t('common.more')}
                        </Button>
                    </Link>
                )}
                {showingsCount > INITIAL_SHOWINGS_COUNT ? (
                    <button
                        className={classnames('w-full flex justify-center items-center mt-auto space-x-6 pb-12')}
                        onClick={() => {
                            track('Expanded Premiere Section', {
                                deviceWidth: window.innerWidth,
                                deviceHeight: window.innerHeight
                            });
                            setIsExpanded(!isExpanded);
                        }}>
                        <span className="text-body-12">
                            {isExpanded
                                ? t('home.show-less')
                                : t('home.more-showings', {
                                      count: Math.min(showingsCount, MAX_VISIBLE_SHOWINGS) - INITIAL_SHOWINGS_COUNT
                                  })}
                        </span>
                        <IconDown16 className={classnames('duration-150', { 'rotate-180': isExpanded })} />
                    </button>
                ) : (
                    <div className="h-[30px]" />
                )}
            </CardWrapper>
        </div>
    );
}
